import { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdatePeriodLock } from '@legacy/apis/exchangeRateAPI';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@legacy/components/mui-components';

interface LockPeriodModalProps {
  periodId: number;
  currentPeriodIsLocked: boolean;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const LockPeriodModal: FC<LockPeriodModalProps> = ({
  periodId,
  currentPeriodIsLocked,
  isOpen,
  setIsOpen,
}) => {
  const { t } = useTranslation('systemAdminExchangeRate');
  const { mutateAsync: updateLockPeriod } = useUpdatePeriodLock();

  const updateLockPeriodAction = async () => {
    try {
      const response = await updateLockPeriod({
        periodId,
        isLockPeriod: !currentPeriodIsLocked,
      });
      if (response) {
        return response;
      }
    } catch (e) {
      // TODO: We should notify the user that an error has occurred
      // for now we are just logging the error as we don't have a reasonable way to
      // tell what went wrong.
      // eslint-disable-next-line no-console
      console.error('Error updating period lock', e);
    } finally {
      setIsOpen(false);
    }
    return null;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      aria-labelledby="dialog-lock-period"
      aria-describedby="dialog-lock-period-desc"
    >
      <DialogTitle id="dialog-lock-period">
        {currentPeriodIsLocked
          ? t('modals.UnlockPeriodTitle')
          : t('modals.LockPeriodTitle')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="dialog-lock-period-desc">
          {currentPeriodIsLocked
            ? t('modals.UnlockPeriodDescription')
            : t('modals.LockPeriodDescription')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => setIsOpen(false)}
          data-automation-id="LockPeriodModalCancelButton"
        >
          {t('modals.CancelButton')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => updateLockPeriodAction()}
          autoFocus
          data-automation-id="LockPeriodModalSaveButton"
        >
          {t('modals.LockPeriodSaveButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LockPeriodModal;
